import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { classes as classesCache, rides as ridesCache } from "../local/db";
import { getManualRideById } from "../store/manualRides";
import { scribePositionsFor } from "../util/scribeAssignment";
import { useScoreContext } from "../context/ScoreContext";

// The seat a scribe chose in a multi-seat class, kept per class so a reload
// mid-ride doesn't drop them back into the other judge's sheet.
const choiceKey = (classId) => `scribe-fe.position.${classId}`;

function readChoice(classId) {
  if (!classId) return null;
  try {
    return sessionStorage.getItem(choiceKey(classId));
  } catch {
    return null;
  }
}

function writeChoice(classId, position) {
  if (!classId) return;
  try {
    sessionStorage.setItem(choiceKey(classId), position);
  } catch {
    // sessionStorage unavailable (private browsing) — the choice just won't
    // survive a reload.
  }
}

/**
 * #627 — resolve the judge position this scribe is scoring a ride at, from the
 * ride's class (Class.scribeAssignments, #396). Cache-first so it works offline;
 * revalidated from score-api when online.
 *
 * Returns { position, positions, choosePosition, loading }:
 *  - one assignment  → `position` is it, `positions` has one entry.
 *  - none            → `position` is null (the scribe isn't on this class).
 *  - two or more     → `position` is the chosen seat (persisted per class), and
 *                      `positions` lists the seats so the caller can offer them.
 */
export function useAssignedPosition(rideId) {
  const api = useScoreApi();
  const { user } = useAuth();
  const { online } = useScoreContext();
  const sub = user?.profile?.sub ?? null;
  const [state, setState] = useState({
    loading: true,
    classId: null,
    positions: [],
  });
  const [chosen, setChosen] = useState(null);
  // #1888 — the key of the last run of this effect that actually settled
  // (any of the setState(loading:false, ...) calls below fired). Lets a
  // same-ride re-run (deps flap: `online` flips on a reconnect drain, or a
  // real venue-wifi blip) tell itself apart from a genuine ride switch, the
  // same way useScoreSheet's load effect does — see that hook's #1888
  // comment. ScoringForm gates its whole tree behind `if (loading ||
  // resolvingPosition) return <spinner>`, and resolvingPosition depends on
  // this hook's `loading`, so unconditionally flipping it true on every
  // effect run unmounts the scoring form on every connectivity blip: same
  // remount, same scroll reset / focus loss (#153) / visible flash (#154)
  // as the useScoreSheet cause, just via the OTHER half of that OR gate.
  const settledKey = useRef(null);

  useEffect(() => {
    if (!rideId) {
      setState({ loading: false, classId: null, positions: [] });
      settledKey.current = null;
      return undefined;
    }
    let cancelled = false;
    const posKey = `${rideId}::${sub}`;
    const keepLocal = settledKey.current === posKey;
    if (!keepLocal) {
      setState((s) => ({ ...s, loading: true }));
    }

    const classFor = async (fetchClass) => {
      const manual = getManualRideById(rideId);
      const ride = manual ?? (await fetchClass.ride());
      const classId = ride?.classId ?? null;
      if (!classId) return { classId: null, klass: null };
      const klass = await fetchClass.klass(classId);
      return { classId, klass };
    };

    (async () => {
      let resolved = null;
      try {
        resolved = await classFor({
          ride: () => ridesCache.get(rideId),
          klass: (id) => classesCache.get(id),
        });
      } catch {
        resolved = null;
      }
      if (!cancelled && resolved?.klass) {
        settledKey.current = posKey;
        setState({
          loading: false,
          classId: resolved.classId,
          positions: scribePositionsFor(resolved.klass, sub),
        });
        setChosen(readChoice(resolved.classId));
      }

      if (!online) {
        if (!cancelled && !resolved?.klass) {
          settledKey.current = posKey;
          setState({ loading: false, classId: resolved?.classId ?? null, positions: [] });
        }
        return;
      }

      try {
        const fresh = await classFor({
          ride: () => api.rides.getRide(rideId).then(unwrap),
          klass: (id) => api.classes.getClass(id).then(unwrap),
        });
        if (cancelled) return;
        settledKey.current = posKey;
        setState({
          loading: false,
          classId: fresh.classId,
          positions: scribePositionsFor(fresh.klass, sub),
        });
        setChosen(readChoice(fresh.classId));
      } catch {
        // Network failed after a cache hit — keep what we have; otherwise
        // settle out of loading with no assignment.
        if (!cancelled && !resolved?.klass) {
          settledKey.current = posKey;
          setState({ loading: false, classId: null, positions: [] });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [api, rideId, sub, online]);

  const choosePosition = useCallback(
    (position) => {
      writeChoice(state.classId, position);
      setChosen(position);
    },
    [state.classId],
  );

  const { positions } = state;
  const position =
    positions.length === 1
      ? positions[0]
      : chosen && positions.includes(chosen)
        ? chosen
        : null;

  return { position, positions, choosePosition, loading: state.loading };
}
